import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, User, Menu, X } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { api } from "@/lib/api";

export const usePublishedPolicies = () => {
  const [slugs, setSlugs] = useState([]);
  useEffect(() => { api.get("/content/policy").then((r) => setSlugs(r.data.map((p) => p.slug))).catch(() => {}); }, []);
  return slugs;
};

const POLICY_LINKS = [["shipping", "Shipping", "help"], ["returns", "Returns and refunds", "help"], ["privacy", "Privacy", "legal"], ["terms", "Terms", "legal"], ["credit-terms", "Promotional credit terms", "legal"]];

const NAV = [["/shop", "Shop"], ["/about", "About"], ["/journey", "Our journey"], ["/faq", "FAQ"], ["/contact", "Contact"]];

export const Header = () => {
  const { config, cartCount, user } = useStore();
  const [open, setOpen] = useState(false);
  return (
    <>
      {config && config.mode !== "LIVE" && (
        <div className="bg-[#173B2D] text-[#F8F5E9] text-center text-xs tracking-wide py-2 px-4" data-testid="prelaunch-banner">
          {config.mode === "PRELAUNCH" ? "HONEY NUTS isn't available to buy yet. Join the launch list to hear first." : "Staff test mode — purchases use Stripe TEST payments only."}
        </div>
      )}
      <header className="sticky top-0 z-50 bg-[#F8F5E9]/90 backdrop-blur-md border-b border-[#E5DEC9]">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link" aria-label="HONEY NUTS home">
            <img src="/honey-nuts-logo.png" alt="HONEY NUTS" width="240" height="160" className="h-10 md:h-12 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm" aria-label="Main">
            {NAV.map(([to, label]) => (
              <NavLink key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) => `transition-colors hover:text-[#A66B24] ${isActive ? "text-[#A66B24]" : ""}`}>{label}</NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-4">
            <Link to={user ? "/account" : "/login"} aria-label="Account" data-testid="header-account-link" className="hover:text-[#A66B24]"><User size={20} /></Link>
            <Link to="/cart" aria-label={`Cart, ${cartCount} items`} data-testid="header-cart-link" className="relative hover:text-[#A66B24]">
              <ShoppingBag size={20} />
              {cartCount > 0 && <span className="absolute -top-2 -right-2 bg-[#A66B24] text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center" data-testid="cart-count">{cartCount}</span>}
            </Link>
            <button className="md:hidden" onClick={() => setOpen(!open)} aria-label="Menu" aria-expanded={open} data-testid="mobile-menu-toggle">{open ? <X size={22} /> : <Menu size={22} />}</button>
          </div>
        </div>
        {open && (
          <nav className="md:hidden border-t border-[#E5DEC9] px-5 py-4 flex flex-col gap-4" aria-label="Mobile">
            {NAV.map(([to, label]) => <Link key={to} to={to} onClick={() => setOpen(false)} className="text-base">{label}</Link>)}
          </nav>
        )}
      </header>
    </>
  );
};

export const Footer = () => {
  const { config } = useStore();
  const socials = Object.entries(config?.socials || {});
  const published = usePublishedPolicies();
  const policyLinks = (group) => POLICY_LINKS.filter(([s, , g]) => g === group && published.includes(s))
    .map(([s, label]) => <Link key={s} to={`/policies/${s}`} data-testid={`footer-policy-${s}`}>{label}</Link>);
  return (
    <footer className="mt-24 bg-[#173B2D] text-[#F8F5E9]">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-14 grid gap-10 md:grid-cols-4 text-sm">
        <div className="md:col-span-2">
          <p className="serif text-3xl mb-3">HONEY NUTS</p>
          <p className="opacity-80 max-w-sm">Honey with mixed nuts and seeds. An online delivery business based in Padstow, NSW. Delivery within Australia only; no pickup.</p>
          {config?.business?.email && <a className="block mt-4 underline opacity-90" href={`mailto:${config.business.email}`}>{config.business.email}</a>}
        </div>
        <div className="flex flex-col gap-2">
          <p className="eyebrow !text-[#d9b27a]">Help</p>
          <Link to="/faq">FAQ</Link><Link to="/contact">Contact</Link>{policyLinks("help")}
        </div>
        <div className="flex flex-col gap-2">
          <p className="eyebrow !text-[#d9b27a]">Legal</p>
          {policyLinks("legal")}
          {socials.map(([k, v]) => <a key={k} href={v} target="_blank" rel="noreferrer" className="capitalize">{k}</a>)}
        </div>
      </div>
      <p className="text-center text-xs opacity-60 pb-8">© {new Date().getFullYear()} HONEY NUTS · Prices in AUD</p>
    </footer>
  );
};

export const PageShell = ({ children, narrow }) => (
  <main className={`${narrow ? "max-w-3xl" : "max-w-6xl"} mx-auto px-5 md:px-8 py-12 md:py-20 fade-up`}>{children}</main>
);

export const Field = ({ label, id, error, hint, children }) => (
  <div className="flex flex-col gap-1.5">
    <label htmlFor={id} className="text-sm font-medium">{label}</label>
    {children}
    {hint && <p className="text-xs text-[#5b6b62]">{hint}</p>}
    {error && <p className="text-xs text-red-700" role="alert">{error}</p>}
  </div>
);

export const Notice = ({ tone = "info", children, testid }) => (
  <div role={tone === "error" ? "alert" : "status"} data-testid={testid}
    className={`rounded-md px-4 py-3 text-sm border ${tone === "error" ? "border-red-300 bg-red-50 text-red-800" : tone === "warn" ? "border-[#A66B24]/40 bg-[#A66B24]/10 text-[#5a3a12]" : "border-[#173B2D]/20 bg-[#173B2D]/5"}`}>
    {children}
  </div>
);

export const ProductVisual = ({ image, className = "" }) =>
  image ? (
    <figure className={`relative overflow-hidden rounded-lg bg-[#EFE8D2] ${className}`}>
      <img src={image.src} alt={image.alt || ""} className="w-full h-full object-cover" loading="eager" fetchpriority="high" decoding="async" />
      {image.is_concept && <span className="absolute top-3 left-3 text-[10px] tracking-widest uppercase px-2.5 py-1 rounded-full bg-[#F8F5E9]/90 text-[#7a4d18] border border-[#A66B24]/30" data-testid="concept-badge">Concept image</span>}
    </figure>
  ) : (
    <div className={`honeycomb-bg rounded-lg flex flex-col items-center justify-center text-center p-8 ${className}`} data-testid="product-photo-placeholder">
      <div className="w-24 h-28 border-2 border-[#A66B24]/50" style={{ clipPath: "polygon(25% 0,75% 0,100% 50%,75% 100%,25% 100%,0 50%)", background: "#A66B2422" }} aria-hidden />
      <p className="mt-4 text-xs tracking-widest uppercase text-[#7a4d18]">Product photography coming soon</p>
    </div>
  );
